<script setup lang="ts">
import { Input, PhoneNumberInput, SearchSelect, Select, TextArea, ToolTip } from '@/components/global';
import { CircleMarkIcon, InfoIcon } from '@/components/svg';

import { IFormField, InputVariant } from '@/types';

const hintIcon = (type?: 'success' | 'info' | 'error') => {
  if (type === 'success') return CircleMarkIcon;
  if (type === 'info') return InfoIcon;
  return null;
};

const props = withDefaults(
  defineProps<{
    fields: IFormField[]
    form: Record<string, any>
    v$?: any
    columns?: number
    variant?: InputVariant
  }>(),
  { columns: 1, variant: 'primary' }
);

const emit = defineEmits<{
  (e: 'update:query', fieldName: string, value: string): void;
  (e: 'load-more-options', fieldName: string): void;
  (e: 'on-blur', fieldName: string): void;
  (e: 'end-icon-click', fieldName: string): void;
}>();

const dynamicColClass = (fieldCol?: number) => {
  const colCount = fieldCol ?? props.columns;
  const colWidth = Math.floor(12 / colCount);
  return `col-md-${colWidth}`;
};

const dynamicVariant = (fieldVariant?: InputVariant): InputVariant => {
  return fieldVariant ?? props.variant;
};

const handleQueryUpdate = (fieldName: string, value: string) => {
  emit('update:query', fieldName, value);
};

const handleLoadMore = (fieldName: string) => {
  emit('load-more-options', fieldName);
};

const handleBlur = (fieldName: string) => {
  props.v$?.[fieldName]?.$touch();
  emit('on-blur', fieldName);
};

const handleEndIconClick = (fieldName: string) => {
  emit('end-icon-click', fieldName);
};
</script>

<template>
  <section class="row w-100 mx-0 gy-2 gy-md-3 gx-0 gx-md-2 gx-xl-3">
    <div
      v-for="field in fields"
      :key="field.name"
      :class="['col-12', dynamicColClass(field.cols)]"
    >
      <div class="w-100 d-flex align-items-center">
        <!-- Phone Input -->
        <PhoneNumberInput
          v-if="field.type === 'phone'"
          v-model="form[field.name]"
          :label="field.label ?? ''"
          :label-icon="field.labelIcon ?? ''"
          :variant="dynamicVariant(field.variant)"
          :testId="field.testId ?? `${field.name}Input`"
          :required="field.required"
          :disabled="field.disabled"
          :readonly="field.readonly"
          @blur="handleBlur(field.name)"
        />

        <!-- Select Input -->
        <Select
          v-else-if="field.type === 'select'"
          v-model="form[field.name]"
          :label="field.label ?? ''"
          :label-icon="field.labelIcon ?? ''"
          :placeholder="field.placeholder ?? ''"
          :options="field.options"
          :variant="dynamicVariant(field.variant)"
          :testId="field.testId ?? `${field.name}Select`"
          :required="field.required"
          :readonly="field.readonly"
          @blur="handleBlur(field.name)"
        />

        <!-- Search Select -->
        <SearchSelect
          v-else-if="field.type === 'search-select'"
          v-model="form[field.name]"
          :label="field.label ?? ''"
          :label-icon="field.labelIcon ?? ''"
          :placeholder="field.placeholder ?? ''"
          :options="field.options"
          :variant="dynamicVariant(field.variant)"
          :testId="field.testId ?? `${field.name}SearchSelect`"
          :required="field.required"
          :loading-options="field.loadingOptions"
          :loading-more-options="field.loadingMoreOptions"
          :readonly="field.readonly"
          @blur="handleBlur(field.name)"
          @update:query="handleQueryUpdate(field.name, $event)"
          @load-more-options="handleLoadMore(field.name)"
        />

        <!-- TextArea -->
        <TextArea
          v-else-if="field.type === 'textarea'"
          v-model="form[field.name]"
          :label="field.label ?? ''"
          :label-icon="field.labelIcon ?? ''"
          :placeholder="field.placeholder ?? ''"
          :variant="dynamicVariant(field.variant)"
          :rows="field.rows ?? 3"
          :testId="field.testId ?? `${field.name}Textarea`"
          :required="field.required"
          :disabled="field.disabled"
          :readonly="field.readonly"
          @blur="handleBlur(field.name)"
        />

        <!-- Input (Default) -->
        <div v-else class="position-relative w-100">
          <!-- Label with Tooltip Support -->
          <div v-if="field.labelTooltip" class="mb-1 d-flex align-items-center">
            <span class="input-label d-flex align-items-center">
              {{ field.label }}
              <span v-if="field.required">&ast;</span>
            </span>

            <ToolTip
              v-if="field.labelTooltip"
              class="ms-1"
              style="z-index: 1; max-height: 1.375rem;"
              :placement="field.labelTooltip.placement ?? 'right-start'"
              :color="field.labelTooltip.color ?? 'var(--ch-white)'"
              :bgColor="field.labelTooltip.bgColor ?? 'var(--ch-secondary)'"
              :maxWidth="field.labelTooltip.maxWidth ?? '17.3rem'"
              :text="field.labelTooltip.text"
              :labelIcon="field.labelTooltip.labelIcon ?? field.labelIcon"
            />
          </div>

          <Input
            v-model="form[field.name]"
            :type="field.type ?? 'text'"
            :label="field.labelTooltip ? '' : (field.label ?? '')"
            :label-icon="field.labelTooltip ? '' : (field.labelIcon ?? '')"
            :placeholder="field.loading ? '' : (field.placeholder ?? '')"
            :variant="dynamicVariant(field.variant)"
            :testId="field.testId ?? `${field.name}Input`"
            :required="field.required"
            :disabled="field.disabled || field.loading"
            :readonly="field.readonly"
            :minlength="field.minlength"
            :maxlength="field.maxlength"
            :min="field.min"
            :max="field.max"
            :icon="field.icon"
            :position="field.iconPosition"
            :prefix="field.prefix"
            :suffix="field.suffix"
            :autofocus="field.autofocus"
            @keypress="field.onKeypress"
            @paste="field.onPaste"
            @focus="field.onFocus"
            @blur="handleBlur(field.name)"
            @endIconClick="handleEndIconClick(field.name)"
          />

          <!-- Loading Overlay -->
          <div
            v-if="field.loading"
            class="position-absolute top-50 mt-1 ms-2 d-flex align-items-center"
            style="z-index: 5;"
          >
            <span
              class="me-2"
              style="color: var(--ch-black-30); font-size: var(--font-xs);"
            >
              {{ field.loadingText ?? 'Loading...' }}
            </span>
            <div
              class="spinner-border spinner-border-sm text-secondary"
              role="status"
            >
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>

        <!-- Outer Suffix -->
        <span
          v-if="field.outerSuffix"
          class="outer-suffix align-items-center"
          :style="field.label && { marginTop: '1.5rem' }"
        >{{ field.outerSuffix }}</span>
      </div>

      <!-- Error Message -->
      <div
        class="form-error"
        v-if="v$ && v$[field.name] && v$[field.name].$error"
        :data-testid="field.testId ? `${field.testId}Error` : `${field.name}Error`"
      >
        <small>
          {{ v$[field.name].$errors[0].$message }}
        </small>
      </div>

      <!-- Hint / Feedback Message -->
      <div
        v-else-if="field.hint"
        :class="['form-hint', `form-hint--${field.hint.type ?? 'info'}`]"
        :data-testid="field.testId ? `${field.testId}Hint` : `${field.name}Hint`"
      >
        <component
          :is="hintIcon(field.hint.type)"
          v-if="hintIcon(field.hint.type)"
          class="form-hint-icon"
          fill="currentColor"
          stroke="currentColor"
        />
        <small>
          {{ field.hint.text }}
        </small>
      </div>
    </div>
  </section>
</template>

<style scoped>
.outer-suffix {
  margin-left: 0.5rem;
  font-size: var(--font-sm);
  letter-spacing: -3%;
}

.input-label {
  margin-bottom: 0;
  font-size: var(--font-xs);
  font-weight: 400;
  text-transform: capitalize;
}

.form-hint {
  display: flex;
  align-items: center;
  margin-top: 0.25rem;
  gap: 0.25rem;
}

.form-hint small {
  font-size: var(--font-xxs);
}

.form-hint-icon {
  flex-shrink: 0;
  width: 0.875rem;
  height: 0.875rem;
}

.form-hint--success {
  color: var(--ch-secondary);
}

.form-hint--info {
  color: var(--ch-blue-200);
}

.form-hint--error {
  color: var(--ch-danger);
}

@media screen and (min-width: 1024px) {
  .input-label {
    font-size: var(--font-sm);
  }

  .form-hint small {
    font-size: var(--font-xs);
  }

  .form-hint-icon {
    width: 1rem;
    height: 1rem;
  }
}
</style>